import { createContext, useContext } from 'react';
import { VisionClient, type EventHandler } from '@akavirtual/deai-web-core';

/**
 * Defines the API that each VisionCanvas makes available to its descendants.
 */
type VisionCanvasContextType = {
    /** A direct reference to the VisionClient instance, for advanced use cases or debugging. */
    client: VisionClient | null;

    /** The API for invoking a method on a specific component in the engine. */
    invoke: (type: string, payload: { entityName: string; [key: string]: any }) => Promise<any>;

    /** The API for querying a specific property from a component in the engine. */
    queryData: (payload: { entityName: string; componentName: string; propertyName: string }) => any;

    /** Broadcasts a global event from the UI into the engine's UIBridge event bus. */
    broadcast: (type: string, payload?: any) => void;

    listenOn: (eventType: string, handler: EventHandler) => void;
    listenOff: (eventType: string, handler: EventHandler) => void;

    /** Indicates whether the underlying game logic has signaled that it is ready. */
    isGameReady: boolean;

    /** Resets the Vision instance and reloads the scene. */
    reset: () => void;
};

// Create the context with default, "empty" values.
export const VisionCanvasContext = createContext<VisionCanvasContextType>({
    client: null,
    invoke: async () => console.warn('VisionCanvasContext not yet initialized.'),
    queryData: () => console.warn('VisionCanvasContext not yet initialized.'),
    broadcast: () => console.warn('VisionCanvasContext not yet initialized.'),
    listenOn: () => console.warn('VisionCanvasContext not yet initialized.'),
    listenOff: () => console.warn('VisionCanvasContext not yet initialized.'),
    isGameReady: false,
    reset: () => console.warn('VisionCanvasContext not yet initialized.'),
});

/**
 * A custom hook that provides easy access to the VisionCanvasContext.
 */
export const useVisionCanvas = () => useContext(VisionCanvasContext);
